import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield, Settings, X, Check } from 'lucide-react';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true, // Always required
  analytics: false,
  marketing: false,
  preferences: false
};

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_PREFERENCES_KEY = 'cookie_preferences';

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setIsVisible(true);
    } else {
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setIsVisible(false);
    
    // Dispatch event for analytics/marketing scripts
    window.dispatchEvent(new CustomEvent('cookie-consent-changed', { 
      detail: prefs 
    }));
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    });
  };

  const acceptNecessary = () => {
    saveConsent(defaultPreferences);
  };

  const saveSelection = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Cannot toggle necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      >
        <Card className="max-w-4xl mx-auto shadow-2xl border-border bg-background">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 rounded-full bg-brand-purple/10">
                <Cookie className="w-6 h-6 text-brand-purple" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-foreground mb-2">
                  Cookie-Einstellungen
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Wir verwenden Cookies und ähnliche Technologien, um dir die bestmögliche Erfahrung zu bieten. 
                  Diese Website nutzt Dienste wie Supabase (Datenbank & Authentifizierung), Font Awesome (Icons) 
                  und speichert anonymisierte Nutzungsdaten zur Analyse. Du kannst selbst entscheiden, welche Cookies du zulassen möchtest.
                </p>
              </div>
            </div>

            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 space-y-4 overflow-hidden"
                >
                  {/* Necessary Cookies */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-green-600" />
                      <div>
                        <Label className="font-semibold text-foreground">Notwendig</Label>
                        <p className="text-xs text-muted-foreground">
                          Erforderlich für Website-Funktionen (Session, Auth, Supabase)
                        </p>
                      </div>
                    </div>
                    <Switch checked={true} disabled className="opacity-50" />
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50">
                    <div className="flex items-center gap-3">
                      <Settings className="w-5 h-5 text-brand-blue" />
                      <div>
                        <Label className="font-semibold text-foreground">Analyse</Label>
                        <p className="text-xs text-muted-foreground">
                          Anonymisierte Seitenaufrufe und Nutzungsstatistiken
                        </p>
                      </div>
                    </div>
                    <Switch 
                      checked={preferences.analytics} 
                      onCheckedChange={() => togglePreference('analytics')} 
                    />
                  </div>

                  {/* Marketing Cookies */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50">
                    <div className="flex items-center gap-3">
                      <Cookie className="w-5 h-5 text-brand-pink" />
                      <div>
                        <Label className="font-semibold text-foreground">Marketing</Label>
                        <p className="text-xs text-muted-foreground">
                          Für personalisierte Werbung
                        </p>
                      </div>
                    </div>
                    <Switch 
                      checked={preferences.marketing} 
                      onCheckedChange={() => togglePreference('marketing')} 
                    />
                  </div>

                  {/* Preference Cookies */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50">
                    <div className="flex items-center gap-3">
                      <Settings className="w-5 h-5 text-brand-purple" />
                      <div>
                        <Label className="font-semibold text-foreground">Präferenzen</Label>
                        <p className="text-xs text-muted-foreground">
                          Speichert deine Einstellungen
                        </p>
                      </div>
                    </div>
                    <Switch 
                      checked={preferences.preferences} 
                      onCheckedChange={() => togglePreference('preferences')} 
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowDetails(!showDetails)}
                className="w-full sm:w-auto"
              >
                <Settings className="w-4 h-4 mr-2" />
                {showDetails ? 'Details ausblenden' : 'Einstellungen'}
              </Button>
              
              <div className="flex items-center gap-3 w-full sm:w-auto sm:ml-auto">
                <Button 
                  variant="outline" 
                  onClick={acceptNecessary}
                  className="flex-1 sm:flex-none"
                >
                  <X className="w-4 h-4 mr-2" />
                  Nur Notwendige
                </Button>
                
                {showDetails ? (
                  <Button 
                    onClick={saveSelection}
                    className="flex-1 sm:flex-none bg-brand-purple hover:bg-brand-purple/90"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Auswahl speichern
                  </Button>
                ) : (
                  <Button 
                    onClick={acceptAll}
                    className="flex-1 sm:flex-none bg-brand-purple hover:bg-brand-purple/90"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Alle akzeptieren
                  </Button>
                )}
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-4 text-center">
              Weitere Informationen findest du in unserer{' '}
              <a href="/seite/datenschutz" className="underline hover:text-foreground">
                Datenschutzerklärung
              </a>
              {' '}und auf der{' '}
              <a href="/credits" className="underline hover:text-foreground">
                Credits-Seite
              </a>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;
